import React, { createContext, useContext, useState } from 'react';
import { CloudData } from '../../../data/cloud-data.js';
import { Configuration, LeatherTypes, SeatBackStyles, SeatPleatingStyles, TunnelSidesOptions } from '../../../data/configuration.js';
import { InteriorConfigurator } from '../../../configurator/interior-configurator.js';
import { VehicleView } from '../../vehicle-view.js';
import { SectionPanel, SectionItem, ItemProps } from '../../view-content.js';
import SelectedSectionItem from '../../ui/selected-section-item.tsx';
import ViewContentRows from '../../ui/view-content-rows.tsx';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface LeatherViewProps {
	interiorConfigurator: InteriorConfigurator;
	onBackButtonClicked: () => void;
}

interface LeatherTypeViewProps {
	leatherType: LeatherTypes;
	isClosed?: boolean;
	onPanelOpenClose?: (isOpen: boolean) => void;
}

interface LeatherItemsProps {
	leatherType: LeatherTypes;
}

interface LeatherItemProps {
	id: string;
}

interface LeatherViewContextType {
	selectedLeatherItemId: string;
	selectedLeatherItemName: string;
	handleLeatherItemClicked: (id: string) => void;
	selectedTunnelSideId: string;
	handleTunnelSideItemClicked: (id: string) => void;
	selectedUpholsteryContrastItemId: string;
	handleUpholsteryContrastItemClicked: (id: string) => void;
}

// one flag per upholstery type, as the paintwork view holds one per paint type and the wheels view one per wheel type. both models
// offer all three types, so every flag is read on both.
type LeatherTypesAccordionState = {
	isTwoTonePanelClosed: boolean;
	isPebbleGrainPanelClosed: boolean;
	isSoftGrainPanelClosed: boolean;
};

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export function LeatherView(props: LeatherViewProps) {

	// initial values.
	const initialLeatherItemId = Configuration.leatherStyle;
	const initialLeatherItemName = () => getItemName(initialLeatherItemId);
	const initialLeatherType = CloudData.getLeatherTypesForId(initialLeatherItemId).leatherType;
	const initialLeatherTypeAccordionState = getAccordionStateForLeatherType(initialLeatherType);
	const initialTunnelSideId = Configuration.tunnelSidesStyle;
	const initialTunnelSideName = () => getItemName(initialTunnelSideId);
	const initialUpholsteryContrastItemId = Configuration.upholsteryContrastOption;
	const initialUpholsteryContrastItemName = () => getItemName(initialUpholsteryContrastItemId);

	// states.
	const [selectedLeatherItemId, setSelectedLeatherItemId] = useState(initialLeatherItemId);
	const [selectedLeatherItemName, setSelectedLeatherItemName] = useState(initialLeatherItemName);
	const [leatherTypeAccordionState, setLeatherTypeAccordionState] = useState<LeatherTypesAccordionState>(initialLeatherTypeAccordionState);
	const [selectedTunnelSideId, setSelectedTunnelSideId] = useState(initialTunnelSideId);
	const [selectedTunnelSideName, setSelectedTunnelSideName] = useState(initialTunnelSideName);
	const [selectedUpholsteryContrastItemId, setSelectedUpholsteryContrastItemId] = useState(initialUpholsteryContrastItemId);
	const [selectedUpholsteryContrastItemName, setSelectedUpholsteryContrastItemName] = useState(initialUpholsteryContrastItemName);

	// handlers.

	const handleUpholsteryContrastItemClicked = (id: string) => {

		// if the item is already selected, toggle it to "None" (no contrast option).
		if (id === selectedUpholsteryContrastItemId) {
			id = 'NONE';
		}

		// update the selected item and item name.
		setSelectedUpholsteryContrastItemId(id);
		setSelectedUpholsteryContrastItemName(getItemName(id));

		// update the configuration.
		Configuration.upholsteryContrastOption = id;
		Configuration.updateCosts();

		// select the leather style on the car.
		props.interiorConfigurator.selectLeatherStyle(selectedLeatherItemId, id);
	};

	const handlePanelOpenClose = (leatherTypePanel: LeatherTypes, isOpen: boolean) => {

		// if opening a panel, open it and close the other panels.
		// if closing a panel, just close them all (as the others will be closed anyway).
		setLeatherTypeAccordionState(getAccordionStateForLeatherType(isOpen ? leatherTypePanel : null));
	};

	const handleLeatherItemClicked = (id: string) => {

		// take the upholstery type from the item clicked rather than from a separately held "selected type", which the accordion no
		// longer needs - and which could lag the click in any case.
		const leatherType = CloudData.getLeatherTypesForId(id).leatherType;

		// update the selected item and item name.
		setSelectedLeatherItemId(id);
		setSelectedLeatherItemName(getItemName(id));

		// reset the contrast option if the current leather type doesn't support it.
		let localSelectedUpholsteryContrastItemId = selectedUpholsteryContrastItemId;
		if (leatherType === LeatherTypes.TwoTone && selectedUpholsteryContrastItemId !== 'NONE') {
			setSelectedUpholsteryContrastItemId('NONE');
			setSelectedUpholsteryContrastItemName(getItemName('NONE'));

			// n.b. react state updates are batched and applied asynchronously, so the relevant state variable won't change immediately.
			// we therefore need to use a local var and pass this to the below 'selectLeatherStyle' function.
			localSelectedUpholsteryContrastItemId = 'NONE';
		}

		// update the configuration.
		Configuration.leatherStyle = id;
		Configuration.updateCosts();

		// if the seat stitching is set to 'matching leather', update it to match the newly selected leather.
		// n.b. has to be done before selecting the leather style on the car due to how the "_setOrFadeSeatMaterial" function works in the InteriorConfigurator.
		if (Configuration.seatStitchingOption === Configuration.stitchingMatchingLeatherStyleId) {
			props.interiorConfigurator.selectSeatStitchingStyle(Configuration.seatStitchingOption);
		}

		// select the leather style on the car.
		props.interiorConfigurator.selectLeatherStyle(id, localSelectedUpholsteryContrastItemId);

		// special handling for the seat back style and pleating options if the selected leather type is two-tone.
		// but only applies to the standard model as the 400 two-tone options work differently.
		if (!Configuration.isSupersport400Model()) {

			// if the selected leather type is two-tone...
			if (leatherType === LeatherTypes.TwoTone) {

				// set the appropriate seat back style (updating the configuration and selecting the style on the car).
				Configuration.seatBackStyle = SeatBackStyles.TwoToneTextile;
				Configuration.updateCosts();
				props.interiorConfigurator.selectSeatBackStyle(SeatBackStyles.TwoToneTextile);

				// set the appropriate seat pleating style (updating the configuration and selecting the style on the car).
				Configuration.seatPleatingOption = Configuration.seatPleatingOptionForTwoToneLeather;
				Configuration.updateCosts();
				props.interiorConfigurator.selectSeatPleatingStyle(Configuration.seatPleatingOption);
			}

			// otherwise, if two-tone was selected, set the seat back style to the default leather wrapped style.
			else if (Configuration.seatBackStyle === SeatBackStyles.TwoToneTextile) {

				// update the configuration and select the seat back style on the car.
				Configuration.seatBackStyle = SeatBackStyles.LeatherWrapped;
				Configuration.updateCosts();
				props.interiorConfigurator.selectSeatBackStyle(SeatBackStyles.LeatherWrapped);
			}
		}
	};

	const handleTunnelSideItemClicked = (id: string) => {

		// update the selected item and item name.
		setSelectedTunnelSideId(id);
		setSelectedTunnelSideName(getItemName(id));

		// update the configuration.
		Configuration.tunnelSidesStyle = id;
		Configuration.updateCosts();

		// select the tunnel sides style on the car.
		props.interiorConfigurator.selectTunnelSidesStyle();
		
		// if carpet is selected for the tunnel sides, and the seat pleating option is currently set to the extended S400 style (Supersport 400 only)...
		if (id === TunnelSidesOptions.Carpet && Configuration.seatPleatingOption === SeatPleatingStyles.ExtendedS400) {

			// reset the seat pleating style to the default style (updating the costs).
			Configuration.seatPleatingOption = SeatPleatingStyles.VerticalS400;
			Configuration.updateCosts();

			// select the seat pleating style on the car.
			props.interiorConfigurator.selectSeatPleatingStyle(Configuration.seatPleatingOption);
		}
	}

	// return the HTML.
	return (
		<LeatherViewContext.Provider value={{
			selectedLeatherItemId, selectedLeatherItemName, handleLeatherItemClicked,
			selectedUpholsteryContrastItemId, handleUpholsteryContrastItemClicked,
			handleTunnelSideItemClicked, selectedTunnelSideId
		}}>
			<VehicleView
				onNextButtonClicked={props.onBackButtonClicked}>
				<div id="view-content">
					<div id="view-content-header">
						<h2 id="ga4-interior-upholstery">
							INTERIOR UPHOLSTERY
						</h2>
					</div>

					{/* the upholstery types are an accordion of one panel each, as the paintwork and wheels steps are. two-tone is
						rendered either side of the plain leathers, as the standard model leads with it and the 400 offers it last -
						which is the order each model's type picker used. */}
					{!Configuration.isSupersport400Model() && (
						<LeatherTypeView
							leatherType={LeatherTypes.TwoTone}
							isClosed={leatherTypeAccordionState.isTwoTonePanelClosed}
							onPanelOpenClose={(isOpen) => handlePanelOpenClose(LeatherTypes.TwoTone, isOpen)}
						/>
					)}

					<LeatherTypeView
						leatherType={LeatherTypes.PebbleGrain}
						isClosed={leatherTypeAccordionState.isPebbleGrainPanelClosed}
						onPanelOpenClose={(isOpen) => handlePanelOpenClose(LeatherTypes.PebbleGrain, isOpen)}
					/>

					<LeatherTypeView
						leatherType={LeatherTypes.SoftGrain}
						isClosed={leatherTypeAccordionState.isSoftGrainPanelClosed}
						onPanelOpenClose={(isOpen) => handlePanelOpenClose(LeatherTypes.SoftGrain, isOpen)}
					/>

					{Configuration.isSupersport400Model() && (
						<LeatherTypeView
							leatherType={LeatherTypes.TwoTone}
							isClosed={leatherTypeAccordionState.isTwoTonePanelClosed}
							onPanelOpenClose={(isOpen) => handlePanelOpenClose(LeatherTypes.TwoTone, isOpen)}
						/>
					)}

					{Configuration.isSupersport400Model() && CloudData.getLeatherTypesForId(selectedLeatherItemId).leatherType !== LeatherTypes.TwoTone && (
						<SectionPanel
							subHeading={'CONTRAST'}
							modalContent={CloudData.getModalContent(selectedUpholsteryContrastItemId, CloudData.leatherStyleOptions)}
						>
							<SelectedSectionItem
								itemName={selectedUpholsteryContrastItemName}
								sectionPrice={CloudData.getPrice(selectedUpholsteryContrastItemId, CloudData.leatherStyleOptions, { ref: false })}
							/>
							<ContrastItems />
						</SectionPanel>
					)}

					<SectionPanel
						subHeading={'TUNNEL SIDES'}
						modalContent={CloudData.getModalContent(selectedTunnelSideId, CloudData.leatherStyleOptions)}
					>
						<SelectedSectionItem
							itemName={selectedTunnelSideName}
							sectionPrice={CloudData.getPrice(selectedTunnelSideId, CloudData.leatherStyleOptions, { ref: false })}
						/>
						<TunnelSideItems />
					</SectionPanel>

				</div>
			</VehicleView>
		</LeatherViewContext.Provider>
	);
}

//-----------------------------------------------------------------------------------------
// Public Functions:
//-----------------------------------------------------------------------------------------

// used by views that have a 'matched leather' option in order to display a swatch showing the currently selected leather colour in the chosen type.
// n.b. not to be confused with "getFilePathForLeatherItemId" below, which covers the same IDs but returns the leather option's own image. the two
// differ for every two-tone ID - LP018 is "pebble-grain/black" here (the grain it matches) but "two-tone/pebble-black-grey" there (its own swatch).
//
// !! the values below are not just this folder's filenames - they are the tail of a path built against several image trees, so a value
// here must exist as a file in every one of them:
//   assets/images/configuration-items/interior/leather/<value>.webp                     (seats-view, summary-step-two)
//   assets/images/configuration-items/interior/seat-pleating/<style>/<value>.webp       (seats-view, one per seat pleating style)
// so re-coding or re-categorising a leather colour means renaming or adding the images in ALL of those trees, not only adding the row
// here. nothing checks this - a value with no matching file simply renders a broken image for that colour, and only on the step that
// uses that tree. this has bitten us twice: Plus Four lost its seat pleating icons for British Green Leaf, Cotswold Green, Light Grey,
// Aubergine and Fawn because the colours were re-coded here and the seat-pleating artwork was left under the old names and grains.
export function getFilePathForLeatherStyleId(id: string): string {

	// map the leather style IDs to the file paths for the relevant swatches.
	const filePaths: Record<string, string> = {

		// pebble grain.
		LP001: 'pebble-grain/black',
		LP003: 'pebble-grain/tan',
		LP002: 'pebble-grain/dark-brown',
		LP013: 'pebble-grain/slate',
		LP010: 'pebble-grain/biscuit',
		LP011: 'pebble-grain/cream',
		LP006: 'pebble-grain/british-green',
		LP009: 'pebble-grain/british-blue',

		// soft grain.
		LS002: 'soft-grain/black',
		LS003: 'soft-grain/dark-brown',
		LS001: 'soft-grain/rich-tan',
		LS004: 'soft-grain/deep-red',
		LS005: 'soft-grain/ivory',
		LS010: 'soft-grain/british-green-leaf',
		LS008: 'soft-grain/aubergine',
		LS009: 'soft-grain/fawn',
		LP007: 'soft-grain/light-grey',
		LS011: 'soft-grain/cotswold-green',
		LP004: 'soft-grain/mulberry',

		// two-tone - pebble grain.
		LP018: 'pebble-grain/black',
		LP019: 'pebble-grain/british-blue',
		LP020: 'pebble-grain/slate',
		LP026: 'pebble-grain/dark-brown',
		LP028: 'pebble-grain/british-blue',
		
		// two-tone - soft grain.
		LP021: 'soft-grain/ivory',
		LP024: 'soft-grain/british-green-leaf',
		LP023: 'soft-grain/dark-brown',
		LP027: 'soft-grain/fawn',
		LP029: 'soft-grain/mulberry'
	};

	// return the corresponding path.
	const filePath = filePaths[id];
	if (filePath) {
		return filePath;
	}

	// or return a fail-save default if not found.
	return 'pebble-grain/black';
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

// context to allow any view in the hierarchy to access the selected item id and call the item clicked handler.
const LeatherViewContext = createContext<LeatherViewContextType | undefined>(undefined);

// one panel of the upholstery-type accordion: the heading is the type and the body is that type's colours.
// n.b. the name, price and information shown are the selected leather's, repeated on every panel rather than being the panel's own
// type's - they describe the configuration, so they should not change as panels are opened and closed. this is what the paintwork
// and wheels accordions do.
function LeatherTypeView(props: LeatherTypeViewProps) {

	// get the context and ensure it is valid.
	const context = useContext(LeatherViewContext);
	if (!context) {
		throw new Error('LeatherViewContext must be used within a LeatherView component');
	}

	// return the HTML.
	return (
		<SectionPanel
			subHeading={getTypeName(props.leatherType).toUpperCase()}
			modalContent={CloudData.getModalContent(context.selectedLeatherItemId, CloudData.leatherStyleOptions)}
			isClosed={props.isClosed}
			onPanelOpenClose={props.onPanelOpenClose}
		>
			<SelectedSectionItem
				itemName={context.selectedLeatherItemName}
				sectionPrice={CloudData.getPrice(context.selectedLeatherItemId, CloudData.leatherStyleOptions, { ref: false })}
			/>
			<LeatherItems leatherType={props.leatherType} />
		</SectionPanel>
	);
}

function LeatherItems(props: LeatherItemsProps) {
	switch (props.leatherType) {
		case LeatherTypes.PebbleGrain:
			return <LeatherItemsPebbleGrain />;
		case LeatherTypes.SoftGrain:
			return <LeatherItemsSoftGrain />;
		default:

			// the standard and 400 models both have two-tone leather options, but with different items in each.
			return Configuration.isSupersport400Model() ? <LeatherItemsTwoTone400 /> : <LeatherItemsTwoTone />;
	}
};

function LeatherItemsTwoTone() {
	return (
		<ViewContentRows>
			<LeatherItem id={'LP018'} />
			<LeatherItem id={'LP019'} />
			<LeatherItem id={'LP020'} />
			<LeatherItem id={'LP021'} />
			<LeatherItem id={'LP024'} />
			<LeatherItem id={'LP023'} />
		</ViewContentRows>
	);
}

function LeatherItemsTwoTone400() {
	return (
		<ViewContentRows>
			<LeatherItem id={'LP026'} />
			<LeatherItem id={'LP027'} />
			<LeatherItem id={'LP028'} />
			<LeatherItem id={'LP029'} />
		</ViewContentRows>
	);
}

function LeatherItemsPebbleGrain() {
	return (
		<ViewContentRows maxRowLength={5}>
			<LeatherItem id={'LP001'} />
			<LeatherItem id={'LP003'} />
			<LeatherItem id={'LP002'} />
			<LeatherItem id={'LP013'} />
			<LeatherItem id={'LP010'} />
			<LeatherItem id={'LP006'} />
			<LeatherItem id={'LP009'} />
			<LeatherItem id={'LP011'} />
		</ViewContentRows>
	);
}

function LeatherItemsSoftGrain() {
	return (
		<ViewContentRows maxRowLength={4}>
			<LeatherItem id={'LS002'} />
			<LeatherItem id={'LS003'} />
			<LeatherItem id={'LS001'} />
			<LeatherItem id={'LS004'} />
			<LeatherItem id={'LP004'} />
			<LeatherItem id={'LS005'} />
			<LeatherItem id={'LS010'} />
			<LeatherItem id={'LS008'} />
			<LeatherItem id={'LS009'} />
			<LeatherItem id={'LP007'} />
			<LeatherItem id={'LS011'} />
		</ViewContentRows>
	);
}

function ContrastItems() {
	return (
		<ViewContentRows>
			<ContrastItem id={'LP025'} imageFilePath={'contrast/black'} />
		</ViewContentRows>
	);
}

function LeatherItem(props: LeatherItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(LeatherViewContext);
	if (!context) {
		throw new Error('LeatherViewContext must be used within a LeatherView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={`assets/images/configuration-items/interior/leather/${getFilePathForLeatherItemId(props.id)}.webp`}
			isSelected={props.id === context.selectedLeatherItemId}
			onItemClicked={context.handleLeatherItemClicked}
		/>
	);
}

function ContrastItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(LeatherViewContext);
	if (!context) {
		throw new Error('LeatherViewContext must be used within a LeatherView component');
	}

	const imageFilePath = `assets/images/configuration-items/interior/leather/${props.imageFilePath}.webp`;

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={imageFilePath}
			isSelected={props.id === context.selectedUpholsteryContrastItemId}
			onItemClicked={() => context.handleUpholsteryContrastItemClicked(props.id)}
		/>
	);
}

function TunnelSideItems() {

	// get the context and ensure it is valid.
	const context = useContext(LeatherViewContext);
	if (!context) {
		throw new Error('LeatherViewContext must be used within a LeatherView component');
	}

	// get the correct image path to show the selected carpet.
	const handleCarpetImagePath = () => {
		const carpetName = CloudData.getName(Configuration.carpetColour, CloudData.carpetColourOptions);

		// get carpet colour to create the image path.
		const carpetNameSplit = carpetName.split(' ');
		const carpetColour = carpetNameSplit[carpetNameSplit.length - 1];

		return `${carpetColour.toLowerCase()}-box-weave`;
	}

	// the leather tunnel sides show the leather currently selected, so the swatch is derived from that selection.
	const leatherImageFilePath = `assets/images/configuration-items/interior/leather/${getFilePathForLeatherItemId(context.selectedLeatherItemId)}.webp`;

	return (
		<ViewContentRows>
			<TunnelSideItem id="LO001" imageFilePath={`assets/images/configuration-items/interior/carpet/${handleCarpetImagePath()}.webp`} />
			<TunnelSideItem id="LO002" imageFilePath={leatherImageFilePath} />
		</ViewContentRows>
	);
}

function TunnelSideItem(props: ItemProps) {

	// get the context and ensure it is valid.
	const context = useContext(LeatherViewContext);
	if (!context) {
		throw new Error('LeatherViewContext must be used within a LeatherView component');
	}

	// return the HTML.
	return (
		<SectionItem
			id={props.id}
			itemName={getItemName(props.id)}
			imageFilePath={props.imageFilePath}
			isSelected={props.id === context.selectedTunnelSideId}
			onItemClicked={context.handleTunnelSideItemClicked}
		/>
	);
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// returns the file path for a leather option's own image - the swatch shown for it in the upholstery colour rows, and on the leather tunnel sides
// option. it is the only place these are named, as the item rows look their own image up from their ID.
// n.b. not to be confused with "getFilePathForLeatherStyleId" above; see the note there for how the two differ.
function getFilePathForLeatherItemId(id: string): string {

	// map the leather style IDs to the file paths for their own swatches.
	const filePaths: Record<string, string> = {

		// pebble grain.
		LP001: 'pebble-grain/black',
		LP003: 'pebble-grain/tan',
		LP002: 'pebble-grain/dark-brown',
		LP013: 'pebble-grain/slate',
		LP010: 'pebble-grain/biscuit',
		LP006: 'pebble-grain/british-green',
		LP009: 'pebble-grain/british-blue',
		LP011: 'pebble-grain/cream',

		// soft grain.
		LS002: 'soft-grain/black',
		LS003: 'soft-grain/dark-brown',
		LS001: 'soft-grain/rich-tan',
		LS004: 'soft-grain/deep-red',
		LP004: 'soft-grain/mulberry',
		LS005: 'soft-grain/ivory',
		LS010: 'soft-grain/british-green-leaf',
		LS008: 'soft-grain/aubergine',
		LS009: 'soft-grain/fawn',
		LP007: 'soft-grain/light-grey',
		LS011: 'soft-grain/cotswold-green',

		// two-tone.
		LP018: 'two-tone/pebble-black-grey',
		LP019: 'two-tone/pebble-british-blue',
		LP020: 'two-tone/pebble-grey',
		LP021: 'two-tone/soft-ivory',
		LP024: 'two-tone/soft-british-green-leaf',
		LP023: 'two-tone/soft-dark-brown',

		// two-tone (Supersport 400).
		LP026: 'two-tone/alcantara-chocolate',
		LP027: 'two-tone/alcantara-fawn',
		LP028: 'two-tone/alcantara-midnight',
		LP029: 'two-tone/alcantara-mulberry'
	};

	// return the corresponding path.
	const filePath = filePaths[id];
	if (filePath) {
		return filePath;
	}

	// or return a default if the ID is not one we hold an image for.
	return 'pebble-grain/black';
}

// returns the accordion state that has the given upholstery type's panel open and every other panel closed, or - given null - has
// them all closed. this is both how the accordion starts, opened on the panel holding the current selection, and what every opening
// or closing of a panel produces.
function getAccordionStateForLeatherType(openLeatherType: LeatherTypes | null): LeatherTypesAccordionState {
	return {
		isTwoTonePanelClosed: openLeatherType !== LeatherTypes.TwoTone,
		isPebbleGrainPanelClosed: openLeatherType !== LeatherTypes.PebbleGrain,
		isSoftGrainPanelClosed: openLeatherType !== LeatherTypes.SoftGrain
	};
}

// looks up the given ID in the options data, returning the name if found or logging an error if not.
function getItemName(id: string): string {
	return CloudData.getName(id, CloudData.leatherStyleOptions);
}

// returns the type name from the given type, used to head that type's accordion panel.
// n.b. we can't just use the string value of the enum as "two-tone" needs to be hyphenated, plus we need different names for the standard and 400 models.
function getTypeName(leatherType: LeatherTypes): string {
	switch (leatherType) {
		case LeatherTypes.PebbleGrain:
			return 'Pebble Grain';
		case LeatherTypes.SoftGrain:
			return 'Soft Grain';
		default:
			return Configuration.isSupersport400Model() ? 'Alcantara Two-Tone' : 'Two-Tone';
	}
}
